import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { getRoleLabel, getRoleLabels, RoleLabels, Roles } from '../auth/rbac'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { api } from '../lib/api'
import { AccountStatusLabel, AvailabilityLabel } from '../lib/labels'
import ListContainer from '../ui/ListContainer'
import PeopleTabs from '../components/PeopleTabs'

export default function UsersPage() {
  const [q, setQ] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(Math.max(1, Number(searchParams.get('page') || '1')))
  const [size, setSize] = useState(Math.max(1, Number(searchParams.get('size') || '10')))
  const [availabilityStatus, setAvailabilityStatus] = useState(searchParams.get('availabilityStatus') || '')
  const [showInvite, setShowInvite] = useState(false)
  const [inviteOrgId, setInviteOrgId] = useState('')
  const [inviteRoles, setInviteRoles] = useState('GA_PLANNER')
  const [inviteExpiresAt, setInviteExpiresAt] = useState('')
  const [inviteMaxUses, setInviteMaxUses] = useState('1')
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', orgId: '1', roles: 'GA_PLANNER', phoneNumber: '' })
  const [orgOptions, setOrgOptions] = useState([])
  const [inviteOrgCategory, setInviteOrgCategory] = useState('')
  const [inviteOrgL1, setInviteOrgL1] = useState('')
  const [inviteOrgL2, setInviteOrgL2] = useState('')
  const [inviteOrgL3, setInviteOrgL3] = useState('')
  const [createOrgCategory, setCreateOrgCategory] = useState('')
  const [createOrgL1, setCreateOrgL1] = useState('')
  const [createOrgL2, setCreateOrgL2] = useState('')
  const [createOrgL3, setCreateOrgL3] = useState('')
  const navigate = useNavigate()
  const { user, hasAnyRole } = useAuth() || { user: null, hasAnyRole: () => false }

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [orgScopeId, setOrgScopeId] = useState(searchParams.get('orgId') || '')
  const [orgScopeOptions, setOrgScopeOptions] = useState([])

  const dq = useDebouncedValue(q, 300)

  const formatDateYmd = React.useCallback(() => {
    const d = new Date()
    const yyyy = String(d.getFullYear())
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${yyyy}${mm}${dd}`
  }, [])
  const [pagination, setPagination] = useState({
    totalElements: 0,
    totalPages: 1,
    currentPage: 0,
    size: 10,
    hasNext: false,
    hasPrevious: false,
  })

  // URL → state 동기화 (같은 /users 라우트에서 search만 바뀌는 케이스 대응)
  useEffect(() => {
    const nextOrgId = searchParams.get('orgId') || ''
    const nextAvail = searchParams.get('availabilityStatus') || ''
    const nextPage = Math.max(1, Number(searchParams.get('page') || '1'))
    const nextSize = Math.max(1, Number(searchParams.get('size') || '10'))
    if (nextOrgId !== orgScopeId) setOrgScopeId(nextOrgId)
    if (nextAvail !== availabilityStatus) setAvailabilityStatus(nextAvail)
    if (nextPage !== page) setPage(nextPage)
    if (nextSize !== size) setSize(nextSize)
    // q는 입력 UX 때문에 여기서 강제 동기화하지 않는다.
    // (필요하면 별도 "필터 초기화" 버튼으로 처리)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()])

  useEffect(() => {
    const next = new URLSearchParams()
    if (dq) next.set('q', dq)
    if (orgScopeId) next.set('orgId', String(orgScopeId))
    if (availabilityStatus) next.set('availabilityStatus', String(availabilityStatus))
    if (page && page !== 1) next.set('page', String(page))
    if (size && size !== 10) next.set('size', String(size))
    setSearchParams(next, { replace: true })
  }, [dq, orgScopeId, availabilityStatus, page, size, setSearchParams])

  // 검색어 변경 시 페이지를 1페이지로 초기화
  useEffect(() => {
    setPage(1)
  }, [dq])

  const listQuery = useQuery({
    queryKey: ['users', { dq, page, size, orgScopeId, availabilityStatus }],
    queryFn: async () => {
      const params = {}
      if (dq) params.q = dq
      if (orgScopeId) params.orgId = Number(orgScopeId)
      if (availabilityStatus) params.availabilityStatus = availabilityStatus
      params.page = page - 1
      params.size = size
      const res = await api.get('/users', { params })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data

      if (payload && Array.isArray(payload.content)) {
        const totalElements = payload.totalElements || payload.content.length || 0
        const totalPages = payload.totalPages || 1
        const number = (payload.number ?? 0)
        const pageSize = payload.size || size
        setPagination({
          totalElements,
          totalPages,
          currentPage: number,
          size: pageSize,
          hasNext: number + 1 < totalPages,
          hasPrevious: number > 0,
        })
      } else if (Array.isArray(payload)) {
        setPagination({
          totalElements: payload.length,
          totalPages: 1,
          currentPage: 0,
          size,
          hasNext: false,
          hasPrevious: false,
        })
      }

      const raw = Array.isArray(payload) ? payload : (payload?.content || payload?.items || [])
      // 테이블에서 기대하는 키로 매핑 + 한국어 라벨 적용
      const accountStatusLabel = (s) => AccountStatusLabel[s] || s || '-'
      const availabilityLabel = (s) => AvailabilityLabel[s] || s || '-'
      const roleKorean = (roles) => Array.isArray(roles) ? getRoleLabels(roles).join(', ') : (RoleLabels[roles] || roles || '-')
      return raw.map((u) => ({
        id: u.id,
        name: u.name || u.fullName || u.email?.split('@')[0],
        email: u.email,
        role: roleKorean(u.roles),
        org: u.orgName || u.org || '-',
        orgParent: u.orgParentName || '-',
        orgRoot: u.orgRootName || '-',
        status: u.status || 'ACTIVE',
        statusLabel: accountStatusLabel(u.status || u.statusName),
        availability: u.availabilityStatus || '-',
        availabilityLabel: availabilityLabel(u.availabilityStatus),
        deputy: u.deputyUserName || (u.deputyUserId ? `ID ${u.deputyUserId}` : '-'),
      }))
    },
    keepPreviousData: true,
  })
  useEffect(() => {
    setItems(listQuery.data || [])
    setLoading(listQuery.isFetching)
    setError(listQuery.isError ? '목록을 불러오지 못했습니다' : null)
  }, [listQuery.data, listQuery.isFetching, listQuery.isError])

  // 조직 범위 옵션 로드 (내 조직 + 하위 조직)
  useEffect(() => {
    let ignore = false
    async function loadOrgScope() {
      try {
        const res = await api.get('/orgs/options', { params: { scope: 'mine' } })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!ignore) setOrgScopeOptions(Array.isArray(payload) ? payload : [])
      } catch {
        if (!ignore) setOrgScopeOptions([])
      }
    }
    loadOrgScope()
    return () => { ignore = true }
  }, [])

  const filtered = useMemo(() => items, [items])

  // 가입신청 승인 화면으로 이동 가능한 역할(= /signups 라우트 RequireRole과 동일 컨셉)
  const SIGNUP_APPROVER_ROLES = useMemo(
    () => [
      Roles.OPERATOR,
      Roles.GA_LEADER,
      Roles.GA_BRANCH_MANAGER,
      Roles.GA_DEPUTY_BRANCH_MANAGER,
      Roles.INSURER_LEADER,
      Roles.INSURER_BRANCH_MANAGER,
      Roles.INSURER_DEPUTY_BRANCH_MANAGER,
    ],
    [],
  )
  const canApproveSignups = hasAnyRole ? hasAnyRole(SIGNUP_APPROVER_ROLES) : false

  // "내가 승인 가능한 가입신청" 대기 인원 수(가벼운 조회: size=1로 totalElements만 사용)
  const signupPendingQuery = useQuery({
    queryKey: ['signups', 'pendingCount'],
    enabled: Boolean(canApproveSignups),
    queryFn: async () => {
      const res = await api.get('/signups', { params: { page: 0, size: 1 } })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      if (payload && typeof payload.totalElements === 'number') return payload.totalElements
      if (payload && Array.isArray(payload.content)) return payload.content.length
      const items = payload?.items
      if (Array.isArray(items)) return items.length
      return 0
    },
    staleTime: 30 * 1000,
  })

  const childrenOf = React.useCallback(
    (parentId) => orgOptions.filter(o => o.parentOrgId === parentId),
    [orgOptions]
  )

  const lifeRootIds = useMemo(
    () =>
      orgOptions
        .filter(o => !o.parentOrgId && o.type === 'INSURER' && (o.name.includes('생명') || o.name.includes('생보')))
        .map(o => o.id),
    [orgOptions]
  )

  const nonLifeRootIds = useMemo(
    () =>
      orgOptions
        .filter(o => !o.parentOrgId && o.type === 'INSURER' && (o.name.includes('손해') || o.name.includes('화재') || o.name.includes('해상')))
        .map(o => o.id),
    [orgOptions]
  )

  const gaRootIds = useMemo(
    () => orgOptions.filter(o => !o.parentOrgId && o.type === 'GA').map(o => o.id),
    [orgOptions]
  )

  const inviteLevel1Options = useMemo(() => {
    if (!inviteOrgCategory) return []
    if (inviteOrgCategory === 'GA') {
      return orgOptions.filter(o => gaRootIds.includes(o.id))
    }
    if (inviteOrgCategory === 'INS_LIFE') {
      return orgOptions.filter(o => lifeRootIds.includes(o.parentOrgId))
    }
    if (inviteOrgCategory === 'INS_NONLIFE') {
      return orgOptions.filter(o => nonLifeRootIds.includes(o.parentOrgId))
    }
    return []
  }, [inviteOrgCategory, orgOptions, gaRootIds, lifeRootIds, nonLifeRootIds])

  const inviteLevel2Options = useMemo(() => {
    if (!inviteOrgL1) return []
    return childrenOf(Number(inviteOrgL1))
  }, [inviteOrgL1, childrenOf])

  const inviteLevel3Options = useMemo(() => {
    if (!inviteOrgL2) return []
    return childrenOf(Number(inviteOrgL2))
  }, [inviteOrgL2, childrenOf])

  const createLevel1Options = useMemo(() => {
    if (!createOrgCategory) return []
    if (createOrgCategory === 'GA') {
      return orgOptions.filter(o => gaRootIds.includes(o.id))
    }
    if (createOrgCategory === 'INS_LIFE') {
      return orgOptions.filter(o => lifeRootIds.includes(o.parentOrgId))
    }
    if (createOrgCategory === 'INS_NONLIFE') {
      return orgOptions.filter(o => nonLifeRootIds.includes(o.parentOrgId))
    }
    return []
  }, [createOrgCategory, orgOptions, gaRootIds, lifeRootIds, nonLifeRootIds])

  const createLevel2Options = useMemo(() => {
    if (!createOrgL1) return []
    return childrenOf(Number(createOrgL1))
  }, [createOrgL1, childrenOf])

  const createLevel3Options = useMemo(() => {
    if (!createOrgL2) return []
    return childrenOf(Number(createOrgL2))
  }, [createOrgL2, childrenOf])

  // 최종 inviteOrgId / createForm.orgId 동기화
  useEffect(() => {
    const selected = inviteOrgL3 || inviteOrgL2 || inviteOrgL1 || ''
    setInviteOrgId(selected)
  }, [inviteOrgL1, inviteOrgL2, inviteOrgL3])

  useEffect(() => {
    const selected = createOrgL3 || createOrgL2 || createOrgL1 || ''
    setCreateForm(prev => ({ ...prev, orgId: selected || prev.orgId }))
  }, [createOrgL1, createOrgL2, createOrgL3])

  // 조직 옵션 로드 (모달 열릴 때 최신화)
  useEffect(() => {
    let ignore = false
    async function loadOrgs() {
      if (!createOpen && !showInvite) return
      try {
        // 운영자/리더는 전체 조직 조회, 그 외는 본인 범위만
        const userHasOperator = Array.isArray(user?.roles) && user.roles.includes('OPERATOR')
        const canSeeAll = userHasOperator || (hasAnyRole ? hasAnyRole([Roles.OPERATOR, Roles.GA_LEADER, Roles.INSURER_LEADER]) : false)
        const scope = canSeeAll ? 'all' : 'mine'
        const res = await api.get('/orgs/options', { params: { scope } })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        let options = Array.isArray(payload) ? payload : []
        // OPERATOR/리더는 모두, 그 외에는 서버에서 이미 mine 스코프로 제한됨
        if (!canSeeAll && user?.orgId) {
          // scope=mine이므로 보정 불필요하지만 안전망으로 현재 org 미포함 시 추가 필터 유지
          options = options.filter(o => String(o.id) === String(user.orgId) || true)
        }
        if (!ignore) setOrgOptions(options)
        // 기본값은 비워 두고, 사용자가 직접 선택하게 함
      } catch (e) {
        if (!ignore) setOrgOptions([])
      }
    }
    loadOrgs()
    return () => { ignore = true }
  }, [createOpen, showInvite, hasAnyRole, user])

  // 리더 여부 및 영역별 역할 제한
  const isGALeader = hasAnyRole && hasAnyRole([Roles.GA_LEADER])
  const isInsLeader = hasAnyRole && hasAnyRole([Roles.INSURER_LEADER])
  const isLeaderOrOperator = (hasAnyRole && hasAnyRole([Roles.OPERATOR, Roles.GA_LEADER, Roles.INSURER_LEADER]))
  // 요구사항: "우리조직 사용자" 탭에서는 (권한 이슈로) 상세 버튼을 숨긴다.
  const canViewOurOrgUserDetail = false

  const allowedRoleOptions = React.useMemo(() => {
    if (hasAnyRole && hasAnyRole([Roles.OPERATOR])) {
      return Object.values(Roles)
    }
    if (isGALeader) {
      return [Roles.GA_MANAGER, Roles.GA_PLANNER]
    }
    if (isInsLeader) {
      return [Roles.INSURER_MANAGER]
    }
    return []
  }, [hasAnyRole, isGALeader, isInsLeader])

  const onInvite = async (e) => {
    e.preventDefault()
    if (!isLeaderOrOperator) return alert('권한이 없습니다')
    try {
      const expiresAt = inviteExpiresAt ? new Date(inviteExpiresAt).toISOString() : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      const response = await api.post('/invites', { orgId: parseInt(inviteOrgId), roles: [inviteRoles], expiresAt, maxUses: parseInt(inviteMaxUses) })
      const inviteCode = response.data?.data?.code || response.data?.code
      const inviteUrl = `${window.location.origin}/invites/accept/${inviteCode}`
      try { navigator.clipboard?.writeText?.(inviteUrl) } catch { }
      alert(`초대 링크가 생성되었습니다:\n${inviteUrl}\n(클립보드에 복사됨)`)
      setShowInvite(false)
      setInviteOrgId('')
      setInviteRoles('GA_PLANNER')
      setInviteExpiresAt('')
      setInviteMaxUses('1')
    } catch (err) {
      alert('초대 생성 실패: ' + (err.response?.data?.message || err.message))
    }
  }

  const onCreate = async (e) => {
    e.preventDefault()
    if (!isLeaderOrOperator) return alert('권한이 없습니다')
    try {
      await api.post('/users', { name: createForm.name, email: createForm.email, password: createForm.password, orgId: parseInt(createForm.orgId, 10), roles: [createForm.roles], phoneNumber: createForm.phoneNumber || undefined })
      setCreateOpen(false)
      setCreateForm({ name: '', email: '', password: '', orgId: '1', roles: 'GA_PLANNER', phoneNumber: '' })
      listQuery.refetch()
    } catch (err) {
      alert('사용자 생성 실패: ' + (err.response?.data?.message || err.message))
    }
  }

  const header = (
    <div className="p-3 border-b">
      <div className="flex items-center justify_between">
        <div className="text-sm text-slate-600">총 {pagination.totalElements}건</div>
      </div>
    </div>
  )

  const table = (
    <table className="min-w-full text-sm">
      <thead className="bg-slate-50 text-slate-600">
        <tr>
          <th className="px-4 py-2 text-left">이름</th>
          <th className="hidden md:table-cell px-4 py-2 text-left">이메일</th>
          <th className="px-4 py-2 text-left">역할</th>
          <th className="px-4 py-2 text-left">소속</th>
          {/* <th className="px-4 py-2 text-left">계정 상태</th> */}
          <th className="hidden md:table-cell px-4 py-2 text-left">근무 상태</th>
          {/* <th className="px-4 py-2 text-left">대직자</th> */}
          <th className="px-4 py-2"></th>
        </tr>
      </thead>
      <tbody>
        {filtered.map(u => (
          <tr key={u.id} className="border-t hover:bg-slate-50">
            <td className="px-4 py-2">{u.name}</td>
            <td className="hidden md:table-cell px-4 py-2">{u.email}</td>
            <td className="px-4 py-2">{u.role}</td>
            <td className="px-4 py-2">
              {(() => {
                const isOperator = Array.isArray(user?.roles) && user.roles.includes('OPERATOR')
                const parts = []
                if (isOperator) {
                  parts.push(u.orgRoot, u.orgParent, u.org)
                } else {
                  // 루트(GA/보험사) 표시는 운영자만. parent가 root와 동일하면 parent도 숨김.
                  if (u.orgParent && u.orgParent !== '-' && u.orgParent !== u.orgRoot) parts.push(u.orgParent)
                  parts.push(u.org)
                }
                return parts.filter(Boolean).filter(p => p !== '-').join(' ')
              })()}
            </td>
            {/* <td className="px-4 py-2">{u.statusLabel}</td> */}
            <td className="hidden md:table-cell px-4 py-2">{u.availabilityLabel}</td>
            {/* <td className="px-4 py-2">{u.deputy}</td> */}
            <td className="px-4 py-2 text-right">
              {canViewOurOrgUserDetail ? (
                <button className="text-brand-navy hover:underline" onClick={() => navigate(`/users/${u.id}`)}>상세</button>
              ) : (
                <span className="text-xs text-slate-400">-</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )

  const footer = (
    <div className="p-3 border-t text-sm text-slate-600 flex items-center justify-between">
      <div>
        페이지 {page} / {Math.max(1, pagination.totalPages)}
      </div>
      <div className="flex items-center gap-2">
        <button
          className="rounded border px-3 py-1 disabled:opacity-50"
          disabled={page <= 1}
          onClick={() => setPage(p => Math.max(1, p - 1))}
        >이전</button>
        <button
          className="rounded border px-3 py-1 disabled:opacity-50"
          disabled={page >= Math.max(1, pagination.totalPages)}
          onClick={() => setPage(p => p + 1)}
        >다음</button>
        <select
          className="rounded border px-2 py-1"
          value={size}
          onChange={(e) => { setPage(1); setSize(Number(e.target.value)) }}
        >
          <option value={10}>10개</option>
          <option value={20}>20개</option>
          <option value={50}>50개</option>
        </select>
      </div>
    </div>
  )

  return (
    <section>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">사용자/담당자</h1>
          <div className="mt-2">
            <PeopleTabs />
          </div>
        </div>
        {isLeaderOrOperator && (
          <div className="flex items-center gap-2">
            <button className="rounded border px-3 py-2 text-sm" onClick={() => setCreateOpen(true)}>+ 사용자 생성</button>
            <button className="rounded bg-brand-navy text-white px-3 py-2 text-sm" onClick={() => setShowInvite(true)}>+ 초대 링크 생성</button>
          </div>
        )}
      </div>

      {canApproveSignups && (signupPendingQuery.data || 0) > 0 && (
        <div className="mt-3 rounded border bg-amber-50 px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-sm text-slate-800">
            <span className="font-semibold">가입신청 승인 대기 인원이 있습니다.</span>{' '}
            <span className="text-slate-700">({signupPendingQuery.data}명)</span>
          </div>
          <button
            type="button"
            className="rounded bg-brand-navy text-white px-3 py-2 text-sm"
            onClick={() => navigate('/signups')}
          >
            승인하러 이동
          </button>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <input className="w-full md:w-80 rounded border px-3 py-2 text-sm" placeholder="검색: 이름/이메일/역할/소속" value={q} onChange={(e) => setQ(e.target.value)} />
        <select
          className="hidden md:inline-flex rounded border px-3 py-2 text-sm"
          value={availabilityStatus}
          onChange={(e) => { setAvailabilityStatus(e.target.value || ''); setPage(1) }}
          title="근무 상태 필터"
        >
          <option value="">근무 상태(전체)</option>
          <option value="ON_DUTY">근무중</option>
          <option value="VACATION">휴가</option>
          <option value="TRAINING">교육</option>
          <option value="AWAY">부재중</option>
        </select>
        {/* 조직 범위 선택: 내 조직 + 하위 조직 */}
        <select
          className="rounded border px-3 py-2 text-sm"
          value={orgScopeId}
          onChange={(e) => { setOrgScopeId(e.target.value); setPage(1) }}
        >
          <option value="">전체보기</option>
          {orgScopeOptions.map(o => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
        <button className="hidden md:inline-flex rounded border px-3 py-2 text-sm" onClick={async () => {
          try {
            const params = { q: dq || undefined, orgId: orgScopeId ? Number(orgScopeId) : undefined, availabilityStatus: availabilityStatus || undefined }
            const res = await api.get('/users/export', { params, responseType: 'blob' })
            const url = URL.createObjectURL(res.data)
            const a = document.createElement('a')
            a.href = url
            a.download = `people_our_users_${formatDateYmd()}.csv`
            a.click()
            URL.revokeObjectURL(url)
          } catch { }
        }}>CSV 내보내기</button>
      </div>

      <div className="mt-4">
        <ListContainer loading={loading} error={error} onRetry={() => listQuery.refetch()} header={header} table={table} footer={footer} />
      </div>

      {showInvite && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4">
          <form onSubmit={onInvite} className="w-full max-w-sm rounded bg-white p-6 space-y-4">
            <h2 className="text-lg font-semibold">초대 링크 생성</h2>
            <div>
              <label className="block text-sm">조직 (이름 선택)</label>
              <div className="grid grid-cols-1 gap-2">
                <select
                  className="mt-1 w-full rounded border px-3 py-2"
                  value={inviteOrgCategory}
                  onChange={(e) => {
                    const v = e.target.value
                    setInviteOrgCategory(v)
                    setInviteOrgL1('')
                    setInviteOrgL2('')
                    setInviteOrgL3('')
                  }}
                  required
                >
                  <option value="">구분 선택: 생보 / 손보 / GA</option>
                  {lifeRootIds.length > 0 && <option value="INS_LIFE">생보(생명보험)</option>}
                  {nonLifeRootIds.length > 0 && <option value="INS_NONLIFE">손보(손해보험)</option>}
                  {gaRootIds.length > 0 && <option value="GA">GA</option>}
                </select>
                <select
                  className="w-full rounded border px-3 py-2"
                  value={inviteOrgL1}
                  onChange={(e) => {
                    const v = e.target.value
                    setInviteOrgL1(v)
                    setInviteOrgL2('')
                    setInviteOrgL3('')
                  }}
                  disabled={!inviteOrgCategory || inviteLevel1Options.length === 0}
                  required
                >
                  <option value="">{inviteOrgCategory ? '상위 조직 선택' : '먼저 구분을 선택하세요'}</option>
                  {inviteLevel1Options.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                <select
                  className="w-full rounded border px-3 py-2"
                  value={inviteOrgL2}
                  onChange={(e) => {
                    const v = e.target.value
                    setInviteOrgL2(v)
                    setInviteOrgL3('')
                  }}
                  disabled={inviteLevel2Options.length === 0}
                >
                  <option value="">1차 하위 조직 (선택)</option>
                  {inviteLevel2Options.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                <select
                  className="w-full rounded border px-3 py-2"
                  value={inviteOrgL3}
                  onChange={(e) => setInviteOrgL3(e.target.value)}
                  disabled={inviteLevel3Options.length === 0}
                >
                  <option value="">2차 하위 조직 (선택)</option>
                  {inviteLevel3Options.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-slate-500 mt-1">내 조직과 하위 조직만 표시됩니다.</p>
            </div>
            <div>
              <label className="block text-sm">역할</label>
              <select className="mt-1 w-full rounded border px-3 py-2" value={inviteRoles} onChange={(e) => setInviteRoles(e.target.value)}>
                {allowedRoleOptions.map(code => (
                  <option key={code} value={code}>{getRoleLabel(code)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm">만료일 (선택)</label>
              <input className="mt-1 w-full rounded border px-3 py-2" type="datetime-local" value={inviteExpiresAt} onChange={(e) => setInviteExpiresAt(e.target.value)} />
              <p className="text-xs text_gray-500 mt-1">비워두면 7일 후로 설정됩니다</p>
            </div>
            <div>
              <label className="block text-sm">최대 사용 횟수</label>
              <input className="mt-1 w_full rounded border px-3 py-2" type="number" min="1" value={inviteMaxUses} onChange={(e) => setInviteMaxUses(e.target.value)} required />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => setShowInvite(false)}>취소</button>
              <button type="submit" className="rounded bg-brand-navy text_white px-3 py-2 text-sm">초대 링크 생성</button>
            </div>
          </form>
        </div>
      )}

      {createOpen && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4">
          <form onSubmit={onCreate} className="w-full max-w-sm rounded bg-white p-6 space-y-4">
            <h2 className="text-lg font-semibold">사용자 생성</h2>
            <div>
              <label className="block text-sm">이름</label>
              <input className="mt-1 w-full rounded border px-3 py-2" value={createForm.name} onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))} required />
            </div>
            <div>
              <label className="block text-sm">이메일</label>
              <input type="email" className="mt-1 w-full rounded border px-3 py-2" value={createForm.email} onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))} required />
            </div>
            <div>
              <label className="block text_sm">비밀번호</label>
              <input type="password" className="mt-1 w-full rounded border px-3 py-2" value={createForm.password} onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))} required />
            </div>
            <div>
              <label className="block text-sm">전화번호</label>
              <input className="mt-1 w-full rounded border px-3 py-2" value={createForm.phoneNumber} onChange={(e) => setCreateForm(prev => ({ ...prev, phoneNumber: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm">조직</label>
              <div className="grid grid-cols-1 gap-2">
                <select
                  className="mt-1 w-full rounded border px-3 py-2"
                  value={createOrgCategory}
                  onChange={(e) => {
                    const v = e.target.value
                    setCreateOrgCategory(v)
                    setCreateOrgL1('')
                    setCreateOrgL2('')
                    setCreateOrgL3('')
                  }}
                  required
                >
                  <option value="">구분 선택: 생보 / 손보 / GA</option>
                  {lifeRootIds.length > 0 && <option value="INS_LIFE">생보(생명보험)</option>}
                  {nonLifeRootIds.length > 0 && <option value="INS_NONLIFE">손보(손해보험)</option>}
                  {gaRootIds.length > 0 && <option value="GA">GA</option>}
                </select>
                <select
                  className="w-full rounded border px-3 py-2"
                  value={createOrgL1}
                  onChange={(e) => {
                    const v = e.target.value
                    setCreateOrgL1(v)
                    setCreateOrgL2('')
                    setCreateOrgL3('')
                  }}
                  disabled={!createOrgCategory || createLevel1Options.length === 0}
                  required
                >
                  <option value="">{createOrgCategory ? '상위 조직 선택' : '먼저 구분을 선택하세요'}</option>
                  {createLevel1Options.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                <select
                  className="w-full rounded border px-3 py-2"
                  value={createOrgL2}
                  onChange={(e) => {
                    const v = e.target.value
                    setCreateOrgL2(v)
                    setCreateOrgL3('')
                  }}
                  disabled={createLevel2Options.length === 0}
                >
                  <option value="">1차 하위 조직 (선택)</option>
                  {createLevel2Options.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                <select
                  className="w-full rounded border px-3 py-2"
                  value={createOrgL3}
                  onChange={(e) => setCreateOrgL3(e.target.value)}
                  disabled={createLevel3Options.length === 0}
                >
                  <option value="">2차 하위 조직 (선택)</option>
                  {createLevel3Options.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm">역할</label>
              <select className="mt-1 w-full rounded border px-3 py-2" value={createForm.roles} onChange={(e) => setCreateForm(prev => ({ ...prev, roles: e.target.value }))}>
                {allowedRoleOptions.map(code => (
                  <option key={code} value={code}>{getRoleLabel(code)}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => setCreateOpen(false)}>취소</button>
              <button type="submit" className="rounded bg-brand-navy text_white px-3 py-2 text-sm">생성</button>
            </div>
          </form>
        </div>
      )}
    </section>
  )
}


